import _get from 'lodash/get';
import isMappingUpdated from './isMappingUpdated';
import MappingProps, { BatchAction } from 'types/Mapping';
import {
  ORG_TEAM_LEADER_MAPPING_REF_KEY,
  ORG_TEAM_LEADER_MAPPING_MATCH_KEY,
  PROFILE_ATTR_MAPPING_TYPE_KEY,
  ORG_MAPPING_TYPE_KEY,
  TL_REF_MAPPING_INDEX,
  TL_MATCH_MAPPING_INDEX,
} from 'pages/Directory/consts';

export default function createLogisticalBatchActions(
  mappingCards: any,
  sourceMappings: MappingProps[],
  sourceId: string,
  orgMappingsSet: boolean,
) {
  const initialHierarchyMapping = sourceMappings.find((sm: any) => sm.type === 'hierarchy');
  const logisticalCard = mappingCards.find((card: any) => {
    return card.type === ORG_MAPPING_TYPE_KEY;
  });
  const orgMappings = _get(logisticalCard, `mappings`);
  const initialOrgMappings = _get(logisticalCard, `initialMappings`, []);

  const batchActions: BatchAction[] = [];

  if (!orgMappings) {
    return [];
  }

  const currentMatchMapping = orgMappings[TL_MATCH_MAPPING_INDEX];
  const initialMatchMapping = initialOrgMappings[TL_MATCH_MAPPING_INDEX];
  const currentRefMapping = orgMappings[TL_REF_MAPPING_INDEX];
  const initialRefMapping = initialOrgMappings[TL_REF_MAPPING_INDEX];

  // (note - pearl) Destroy existing hierarchy mapping if
  // - there's an initial mapping
  // - and the current mapping doesn't exist or is incomplete
  if (
    initialHierarchyMapping &&
    initialHierarchyMapping.id &&
    (orgMappings.length === 0 || !currentMatchMapping.source || !currentRefMapping.source)
  ) {
    const tlRefSourceMapping = sourceMappings.find(
      m => m.destination === ORG_TEAM_LEADER_MAPPING_REF_KEY,
    ) as MappingProps;
    const tlMatchSourceMapping = sourceMappings.find(
      m => m.destination === ORG_TEAM_LEADER_MAPPING_MATCH_KEY,
    ) as MappingProps;

    const destroyArray = [];

    if (initialHierarchyMapping) {
      destroyArray.push({ type: 'destroy', id: initialHierarchyMapping.id });
    }

    if (tlRefSourceMapping) {
      destroyArray.push({ type: 'destroy', id: tlRefSourceMapping.id });
    }

    if (tlMatchSourceMapping) {
      destroyArray.push({ type: 'destroy', id: tlMatchSourceMapping.id });
    }

    return destroyArray;
  }

  // (note - pearl) No actions if
  // - org mappings has been set from a different source
  // - and no mappings are updated
  if (
    initialOrgMappings &&
    orgMappingsSet &&
    !isMappingUpdated(initialMatchMapping, currentMatchMapping) &&
    !isMappingUpdated(initialRefMapping, currentRefMapping)
  ) {
    return [];
  }

  // (note - pearl) Build action for match mapping
  buildBatchActions({
    refMapping: currentMatchMapping,
    initialHierarchyMapping: initialMatchMapping,
    sourceMappings,
    batchActions,
    sourceId: _get(initialHierarchyMapping, 'sourceId') || sourceId,
  });

  // (note - pearl) Build action for ref mapping
  buildBatchActions({
    refMapping: currentRefMapping,
    initialHierarchyMapping: initialRefMapping,
    sourceMappings,
    batchActions,
    sourceId: _get(initialHierarchyMapping, 'sourceId') || sourceId,
  });

  // (note - pearl) Create hierarchy mapping if it doesn't exist
  if (
    (!initialHierarchyMapping || !_get(initialHierarchyMapping, 'id')) &&
    currentMatchMapping.source &&
    currentRefMapping.source
  ) {
    batchActions.push({
      type: 'create',
      data: {
        source: ORG_TEAM_LEADER_MAPPING_REF_KEY,
        destination: ORG_TEAM_LEADER_MAPPING_MATCH_KEY,
        isRequired: false,
        isUnique: false,
        type: 'hierarchy',
        sourceId,
      },
    });
  }

  return batchActions;
}

function buildBatchActions({
  batchActions,
  sourceMappings,
  initialHierarchyMapping,
  refMapping,
  sourceId,
}: any) {
  const existingMapping =
    _get(initialHierarchyMapping, 'destination') &&
    sourceMappings.find((m: any) => m.destination === initialHierarchyMapping.destination);

  if (existingMapping && !refMapping) {
    batchActions.push({ type: 'destroy', id: existingMapping.id });
  } else if (!!_get(refMapping, 'source') && (!existingMapping || !refMapping.id)) {
    batchActions.push({
      type: 'create',
      data: {
        source: _get(refMapping, `customProps.mappingSubSource`) || _get(refMapping, 'source', ''),
        destination: _get(refMapping, 'destination'),
        isRequired: false,
        isUnique: false,
        type: PROFILE_ATTR_MAPPING_TYPE_KEY,
        sourceId,
        collectionItemType: _get(refMapping, `collectionItemType`) || null,
        collectionItemCustomType: _get(refMapping, `collectionItemCustomType`) || null,
      },
    });
  } else if (isMappingUpdated(initialHierarchyMapping, refMapping)) {
    const { id, createdAt, customProps, ...smData } = refMapping;

    batchActions.push({
      type: 'update',
      id,
      data: {
        ...smData,
        source: _get(refMapping, `customProps.mappingSubSource`) || _get(refMapping, 'source', ''),
        sourceId,
        type: PROFILE_ATTR_MAPPING_TYPE_KEY,
        collectionItemType: _get(refMapping, `collectionItemType`) || null,
        collectionItemCustomType: _get(refMapping, `collectionItemCustomType`) || null,
      },
    });
  }
}
